import "../styles/manage-template-categories-modal.scss";
import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Modal } from "./modal";
import { GuiButton } from "./global-ui/gui-button";
import { GuiTextbox } from "./global-ui/gui-textbox";
import { Spinner } from "./loading-spinner";
import { useSelector } from "react-redux";
import api from "../api/api";
import { showToast } from "./toast";

/**
 * ManageTemplateCategoriesModal
 *
 * Allows admins to create, edit, and delete template categories
 * without leaving the template creation form.
 * Renders as a right-side slide-in drawer panel.
 *
 * @param {function} onClose - Called when panel is closed; receives `true` if categories changed
 */
export function ManageTemplateCategoriesModal({ onClose }) {
  const token = useSelector((store) => store.user.token);
  const drawerRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changed, setChanged] = useState(false);

  // Add form
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteCount, setConfirmDeleteCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    setLoading(true);
    const result = await api.templateCategories.getAll(token);
    setLoading(false);
    if (result?.success) {
      setCategories(result.categories || []);
    }
  }

  function handleClose() {
    onClose(changed);
  }

  function handleOverlayClick(e) {
    if (drawerRef.current && !drawerRef.current.contains(e.target)) {
      handleClose();
    }
  }

  // ── Add ────────────────────────────────────────────────────────────────────

  async function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) {
      setAddError("Category Name is Required");
      return;
    }
    if (
      categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())
    ) {
      setAddError("A category with this name already exists.");
      return;
    }

    setAdding(true);
    const result = await api.templateCategories.add(token, trimmed);
    setAdding(false);

    if (result?.success) {
      setCategories([{ id: result.id, name: trimmed }, ...categories]);
      setNewName("");
      setAddError("");
      setChanged(true);
    } else {
      setAddError(result?.errorMessage || "Failed to add category.");
    }
  }

  // ── Edit ───────────────────────────────────────────────────────────────────

  function handleEditStart(cat) {
    setEditingId(cat.id);
    setEditingName(cat.name);
    setEditError("");
  }

  function handleEditCancel() {
    setEditingId(null);
    setEditingName("");
    setEditError("");
  }

  async function handleEditSave() {
    const trimmed = editingName.trim();
    if (!trimmed) {
      setEditError("Category Name is Required");
      return;
    }
    if (
      categories.some(
        (c) =>
          c.id !== editingId && c.name.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setEditError("A category with this name already exists.");
      return;
    }

    setSaving(true);
    const result = await api.templateCategories.edit(token, editingId, trimmed);
    setSaving(false);

    if (result?.success) {
      setCategories(
        categories.map((c) =>
          c.id === editingId ? { ...c, name: trimmed } : c,
        ),
      );
      setEditingId(null);
      setEditingName("");
      setEditError("");
      setChanged(true);
    } else {
      setEditError(result?.errorMessage || "Failed to save category.");
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  async function handleDeleteClick(cat) {
    const result = await api.templateCategories.getUsage(token, cat.id);
    setConfirmDeleteCount(result?.success ? result.templateCount : 0);
    setConfirmDeleteId(cat.id);
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteId) return;
    setDeleting(true);
    const result = await api.templateCategories.delete(token, confirmDeleteId);
    setDeleting(false);

    if (result?.success) {
      setCategories((prev) => prev.filter((c) => c.id !== confirmDeleteId));
      setConfirmDeleteId(null);
      setChanged(true);
    } else {
      showToast("error", result?.errorMessage || "Failed to delete category.");
      setConfirmDeleteId(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {ReactDOM.createPortal(
        <div className="mtc-overlay" onMouseDown={handleOverlayClick}>
          <div className="mtc-drawer" ref={drawerRef}>
            {/* Header */}
            <div className="mtc-header">
              <h3>Manage Template Categories</h3>
              <button
                className="mtc-close-btn"
                aria-label="Close"
                onClick={handleClose}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Add Category */}
            <div className="mtc-add-section">
              <p className="mtc-section-label">Add Category</p>
              <div className="mtc-add-row">
                <GuiTextbox
                  name="newCategoryName"
                  label="Category Name"
                  value={newName}
                  onChange={(_, val) => {
                    setNewName(val);
                    setAddError("");
                  }}
                  error={addError}
                  onKeyUp={(e) => e.key === "Enter" && !adding && handleAdd()}
                  maxLength={50}
                />
                <GuiButton
                  className="mtc-add-btn btn-primary"
                  onClick={handleAdd}
                  disabled={adding}
                >
                  {adding ? <Spinner /> : "Add"}
                </GuiButton>
              </div>
            </div>

            {/* Category List */}
            <div className="mtc-list-section">
              <div className="mtc-list-header">
                <span className="mtc-section-label">Categories</span>
              </div>

              {loading ? (
                <div className="mtc-loading">
                  <Spinner />
                </div>
              ) : categories.length === 0 ? (
                <div className="mtc-empty">
                  <p>No categories created yet.</p>
                  <p>Add categories above.</p>
                </div>
              ) : (
                <div className="mtc-list">
                  {categories.map((cat) =>
                    editingId === cat.id ? (
                      /* Edit row */
                      <div key={cat.id} className="mtc-row mtc-row--editing">
                        <div className="mtc-edit-field">
                          <GuiTextbox
                            name="editCategoryName"
                            value={editingName}
                            onChange={(_, val) => {
                              setEditingName(val);
                              setEditError("");
                            }}
                            error={editError}
                            onKeyUp={(e) => {
                              if (e.key === "Enter" && !saving)
                                handleEditSave();
                              if (e.key === "Escape") handleEditCancel();
                            }}
                            maxLength={50}
                          />
                        </div>
                        <div className="mtc-actions">
                          <button
                            className="mtc-icon-btn mtc-icon-btn--save"
                            onClick={handleEditSave}
                            disabled={saving}
                            title="Save"
                          >
                            {saving ? (
                              <Spinner />
                            ) : (
                              <i className="fas fa-check" />
                            )}
                          </button>
                          <button
                            className="mtc-icon-btn mtc-icon-btn--cancel"
                            onClick={handleEditCancel}
                            title="Cancel"
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal row */
                      <div key={cat.id} className="mtc-row">
                        <span className="mtc-name">{cat.name}</span>
                        <div className="mtc-actions">
                          <button
                            className="mtc-icon-btn"
                            onClick={() => handleEditStart(cat)}
                            title="Edit"
                          >
                            <i className="fas fa-pencil" />
                          </button>
                          <button
                            className="mtc-icon-btn mtc-icon-btn--delete"
                            onClick={() => handleDeleteClick(cat)}
                            title="Delete"
                          >
                            <i className="fas fa-times-circle" />
                          </button>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <Modal className="confirm-delete-modal mtc-confirm-delete">
          <h4>Delete Template Category?</h4>
          {confirmDeleteCount > 0 && (
            <p className="mtc-confirm-warning">
              <i className="fas fa-exclamation-triangle" /> This category is
              currently assigned to{" "}
              <strong>
                {confirmDeleteCount} template
                {confirmDeleteCount !== 1 ? "s" : ""}
              </strong>
              . Deleting it will remove the category from{" "}
              {confirmDeleteCount === 1
                ? "that template"
                : "all those templates"}
              .
            </p>
          )}
          <p className="mtc-confirm-text">
            Are you sure you want to delete this template category? Deleting
            Template Category will remove it from all templates it is assigned
            to. This action cannot be undone.
          </p>
          <div className="btn-container">
            <GuiButton
              className="btn-outline"
              onClick={() => setConfirmDeleteId(null)}
              disabled={deleting}
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? <Spinner /> : "Delete"}
            </GuiButton>
          </div>
        </Modal>
      )}
    </>
  );
}
